import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import InvoiceDetailsTab from "./components/InvoiceDetailsTab";
import InvoicesTab from "./components/InvoicesTab";
import TransactionsTab from "./components/TransactionsTab";
import SelectCardDialog from "./components/SelectCardDialog";
import AddNewCardWithStripe from "./components/AddNewCardWithStripe";
import BoughtPackagesCard from "./components/BoughtPackagesCard";
import PlanTab from "./components/PlanTab";

import { billingApiHooks } from "@/api/billingApi";
import { toast } from "react-hot-toast";
import { IInvoice } from "@/interfaces/Ibilling";

const paymentDetails = {
  card: "**** **** **** 5743",
  expiry: "10/27",
  nextBilling: "28 Dec 2024",
  billedOn: "28th of every month",
};

export default function ReportsAndBilling() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("Billing");
  const [invoiceTab, setInvoiceTab] = useState("Invoice details");
  const [selectCardOpen, setSelectCardOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  // API hooks
  const { data: invoiceDetails = {}, isLoading: isLoadingInvoiceDetails } =
    billingApiHooks.useGetInvoiceDetails();
  const { data: cards = [] } = billingApiHooks.usePaymentMethods();
  const setDefaultMutation = billingApiHooks.useSetDefaultPaymentMethod();
  const deleteMutation = billingApiHooks.useDeletePaymentMethod();
  const changePackageMutation = billingApiHooks.useChangePackage();
  // Get the invoices
  const { data: invoices = [] } = billingApiHooks.useGetInvoices();
  // Get the transactions
  const { data: transactions = [] } = billingApiHooks.useGetTransactions();

  // Get the business line packages
  const { data: businessLinePackages = [] } =
    billingApiHooks.useGetBusinessLinePackages();

  // Get the default card
  const defaultCard = cards.find((c: any) => c.is_default);

  // Set default card as selected by default
  useEffect(() => {
    if (cards.length > 0 && selectedCardId === null) {
      setSelectedCardId(defaultCard ? defaultCard.id : cards[0].id);
    }
  }, [cards, selectedCardId, defaultCard]);

  // Check for URL parameter to open card dialog
  useEffect(() => {
    const openCardDialog = searchParams.get("openCardDialog");
    if (openCardDialog === "true") {
      setShowAddCard(true);
      // Remove the parameter from URL after opening dialog
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("openCardDialog");
        return newParams;
      });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="p-8 bg-white min-h-screen">
      <div className="max-w-5xl mx-auto">
        {/* Top Tabs */}
        <div className="flex space-x-8 border-b mb-8">
          {["Billing", "Report", "Plan"].map((t) => (
            <button
              key={t}
              className={`pb-2 px-2 text-lg font-medium border-b-2 transition-colors duration-200 ${
                tab === t
                  ? "border-[#78C7D1] text-gray-900"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Billing Tab Content */}
        {tab === "Billing" && (
          <>
            {/* Cards Row */}
            <div className="flex flex-col md:flex-row gap-6 mb-8">
              {/* Payment Details Card */}
              <div className="flex-1 bg-white rounded-xl shadow p-6 flex flex-col justify-between min-w-[300px]">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold text-gray-700">
                      Payment details
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-blue-900">
                        {defaultCard
                          ? defaultCard.card?.display_brand ||
                            defaultCard.card?.brand ||
                            "Card"
                          : "No Card"}
                      </span>
                      {defaultCard?.is_declined && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Declined
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-2xl font-mono tracking-widest mb-2">
                    {defaultCard?.card?.last4
                      ? `**** **** **** ${defaultCard.card.last4}`
                      : "No card available"}
                  </div>
                  <div className="text-gray-500 text-sm mb-2">
                    {defaultCard ? (
                      <>
                        {defaultCard.card?.display_brand ||
                          defaultCard.card?.brand ||
                          "Card"}{" "}
                        - Expires{" "}
                        {defaultCard.card?.exp_month &&
                        defaultCard.card?.exp_year
                          ? `${defaultCard.card.exp_month}/${defaultCard.card.exp_year}`
                          : "N/A"}
                      </>
                    ) : (
                      "No default card set"
                    )}
                  </div>
                  <div className="text-gray-400 text-xs">
                    Billed on {paymentDetails.billedOn}. Next billing on
                    {paymentDetails.nextBilling}
                  </div>
                </div>
                <button
                  className="mt-6 bg-[#78C7D1] hover:bg-[#4fa3b3] text-white font-semibold px-4 py-2 rounded-lg shadow"
                  onClick={() => setSelectCardOpen(true)}
                >
                  {defaultCard ? "Select card" : "Add card"}
                </button>
              </div>

              {/* Bought Packages Card */}
              <BoughtPackagesCard
                packages={
                  Array.isArray(businessLinePackages)
                    ? businessLinePackages
                    : businessLinePackages?.business_line_packages || []
                }
                onSeeAllPlans={() => setTab("Plan")}
              />
            </div>

            {/* Invoice Section */}
            <div className="bg-white rounded-xl shadow p-6">
              {/* Invoice Tabs */}
              <div className="flex space-x-8 border-b mb-6">
                {["Invoice details", "Invoices", "Transactions"].map((t) => (
                  <button
                    key={t}
                    className={`pb-2 px-2 text-base font-medium border-b-2 transition-colors duration-200 ${
                      invoiceTab === t
                        ? "border-[#78C7D1] text-gray-900"
                        : "border-transparent text-gray-400 hover:text-gray-600"
                    }`}
                    onClick={() => setInvoiceTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {invoiceTab === "Invoice details" &&
                (isLoadingInvoiceDetails ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 text-6xl mb-4">⏳</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Loading invoice details...
                    </h3>
                    <p className="text-gray-500">
                      Please wait while we fetch your invoice information.
                    </p>
                  </div>
                ) : (
                  <InvoiceDetailsTab
                    invoiceDetails={invoiceDetails}
                    onEdit={() => {
                      /* TODO: open edit modal */
                      console.log(
                        "🖊️ EDIT BUTTON CLICKED - Edit invoice details"
                      );
                      console.log("onEdit function called successfully");
                    }}
                  />
                ))}
              {invoiceTab === "Invoices" && (
                <InvoicesTab
                  invoices={((invoices as any)?.invoices as IInvoice[]) || []}
                />
              )}
              {invoiceTab === "Transactions" && (
                <TransactionsTab
                  transactions={(transactions as any)?.charges || []}
                />
              )}
            </div>
          </>
        )}

        {/* Report Tab Content */}
        {tab === "Report" && (
          <div className="bg-white rounded-xl shadow p-6">
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Reports Dashboard
              </h3>
              <p className="text-gray-500">
                Detailed reports and analytics will be displayed here.
              </p>
            </div>
          </div>
        )}

        {/* Plan Tab Content */}
        {tab === "Plan" && (
          <PlanTab
            paymentMethods={cards}
            packages={
              Array.isArray(businessLinePackages)
                ? businessLinePackages
                : businessLinePackages?.business_line_packages || []
            }
            onSetDefault={(id) => {
              setDefaultMutation.mutate(id, {
                onSuccess: () => {
                  toast.success("Default payment method updated successfully");
                },
                onError: () => {
                  toast.error("Failed to set default payment method");
                },
              });
            }}
            onDelete={(id) => {
              deleteMutation.mutate(id, {
                onSuccess: () => {
                  toast.success("Payment method deleted successfully");
                },
                onError: () => {
                  toast.error("Failed to delete payment method");
                },
              });
            }}
            onAddCard={() => setShowAddCard(true)}
            onChangePlan={async (oldPackageId, newPackageId, effectiveFrom) => {
              // Handle changing plan
              console.log(
                "Change plan from:",
                oldPackageId,
                "to:",
                newPackageId
              );

              try {
                const response = await changePackageMutation.mutateAsync({
                  id: oldPackageId,
                  new_package_id: newPackageId,
                  effective_from: effectiveFrom,
                });

                // Return the response for the dialog to handle
                if (response) {
                  // Return the response for the dialog to show summary
                  return response;
                } else {
                  toast.success("Plan change request submitted successfully");
                  return null;
                }
              } catch (error: any) {
                console.error("Error changing plan:", error);

                // Handle 402 Payment Required error
                if (error?.response?.status === 402) {
                  const errorData = error.response.data;
                  if (errorData?.detail?.msg) {
                    toast.error(errorData.detail.msg);
                  } else {
                    toast.error(
                      "Payment failed. Please check your payment method and try again."
                    );
                  }
                } else {
                  toast.error("Failed to change plan. Please try again.");
                }

                throw error; // Re-throw to let the dialog handle the error
              }
            }}
          />
        )}

        <SelectCardDialog
          open={selectCardOpen}
          onClose={() => setSelectCardOpen(false)}
          cards={cards}
          selectedCardId={selectedCardId || ""}
          onSelect={setSelectedCardId}
          onAddNew={() => {
            setShowAddCard(true);
          }}
          onPay={() => alert("Pay")}
          onDelete={(id) => {
            deleteMutation.mutate(id, {
              onSuccess: () => {
                toast.success("Card deleted successfully");
              },
              onError: () => {
                toast.error("Failed to delete card");
              },
            });
          }}
          onSetDefault={(id) => {
            setDefaultMutation.mutate(id, {
              onSuccess: () => {
                toast.success("Default card updated successfully");
              },
              onError: () => {
                toast.error("Failed to set default card");
              },
            });
          }}
        />

        {showAddCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
                onClick={() => setShowAddCard(false)}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div className="p-6 pt-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Add New Payment Method
                </h2>
                <AddNewCardWithStripe
                  onSuccess={() => {
                    setShowAddCard(false);
                    // TODO: refresh cards if needed
                  }}
                  currentCardCount={cards.length}
                  maxCards={2}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
